import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { CreateCompanyModal } from "./companies";
import { LeadFormModal } from "./leads";
import { useLeadDetail, useConvertLead, useDeleteLead } from "../hooks/use-leads";
import type { CreateCompanyInput } from "../hooks/use-companies";
import { useToast } from "../lib/use-toast";
import { useAuth } from "../lib/use-auth";
import { hasPermission } from "../lib/permissions";
import styles from "./LeadDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/leads/$id",
	component: withPermission(LeadDetailPage, "leads", "read"),
});

function LeadDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const leadQuery = useLeadDetail(id);
	const convertLead = useConvertLead();
	const deleteLead = useDeleteLead();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [convertOpen, setConvertOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const { user } = useAuth();
	const canUpdate = hasPermission(user, "leads", "update");
	const canDelete = hasPermission(user, "leads", "delete");

	if (leadQuery.isLoading) return <LoadingSpinner />;
	if (leadQuery.isError || !leadQuery.data) return <ErrorFallback onRetry={leadQuery.refetch} />;

	const lead = leadQuery.data;

	return (
		<div>
			<BackButton fallbackTo="/leads" />
			<PageHeader
				title={lead.companyName || "-"}
				action={
					<div className={styles.actions}>
						{lead.status !== "converted" && (
							<Button onClick={() => setConvertOpen(true)}>{t("convert")}</Button>
						)}
						{canUpdate && (
							<Button variant="ghost" onClick={() => setEditOpen(true)}>
								{t("edit")}
							</Button>
						)}
						{canDelete && (
							<Button variant="danger" onClick={() => setDeleteOpen(true)}>
								{t("delete")}
							</Button>
						)}
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info label={t("legalName")} value={lead.companyName} />
						<Info label={t("email")} value={lead.email} />
						<Info label={t("phone")} value={lead.phone} />
						<Info label={t("tradeName")} value={lead.tradeName} />
						<Info label={t("taxNumber")} value={lead.taxNumber} />
						<Info label={t("taxOffice")} value={lead.taxOffice} />
						<Info label={t("fax")} value={lead.fax} />
						<Info label={t("website")} value={lead.website} />
						<Info label={t("industry")} value={lead.industry} />
						<Info
							label="Adres"
							value={
								[
									lead.address?.street,
									lead.address?.city,
									lead.address?.state,
									lead.address?.postalCode,
									lead.address?.country,
								]
									.filter(Boolean)
									.join(", ") || null
							}
						/>
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge variant={lead.status === "converted" ? "success" : "primary"}>
								{t(lead.status)}
							</Badge>
						</div>
					</div>
				</Card>
			</div>

			<LeadFormModal isOpen={editOpen} onClose={() => setEditOpen(false)} leadId={id} />

			<CreateCompanyModal
				isOpen={convertOpen}
				onClose={() => setConvertOpen(false)}
				title={t("convert")}
				submitLabel={t("convert")}
				convertMode
				initialValues={{
					legalName: lead.companyName ?? "",
					tradeName: lead.tradeName || [lead.firstName, lead.lastName].filter(Boolean).join(" "),
					taxNumber: lead.taxNumber ?? "",
					taxOffice: lead.taxOffice ?? "",
					phone: lead.phone ?? "",
					fax: lead.fax ?? "",
					email: lead.email ?? "",
					website: lead.website ?? "",
					industry: lead.industry ?? "",
					sapGroupCode: lead.sapGroupCode ?? undefined,
					currency: lead.currency ?? "",
					currencies: lead.currency ? [lead.currency] : [],
					status: "customer",
					addressStreet: lead.address?.street ?? "",
					addressCity: lead.address?.city ?? "",
					addressState: lead.address?.state ?? "",
					addressPostalCode: lead.address?.postalCode ?? "",
					addressCountry: lead.address?.country ?? "TÜRKİYE",
				}}
				onSubmit={async (values: CreateCompanyInput) => {
					await convertLead.mutateAsync({ id, data: values });
					setConvertOpen(false);
				}}
			/>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteLead.mutate(id, {
						onSuccess: () => {
							showToast(t("leadDeleteSuccess"));
							void navigate({
								to: "/leads",
								search: {
									page: 1,
									size: 20,
									status: "",
									search: "",
									createdAtFrom: "",
									createdAtTo: "",
								},
							});
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteLead.isPending}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
